// src/components/tutor/TutorChatPanel.tsx
//
// Shared chat surface used by both the /tutor page and the FAB.
// Owns the input box, message list, scroll behavior, error display.
//
// Doesn't manage its own state — receives a UseTutorThread from the parent.
// That keeps thread state hoisted so the FAB and the /tutor page can
// (in the same browser tab) reuse one thread instance if mounted together.

'use client'

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Send, Loader2, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import 'katex/dist/katex.min.css'
import Diagram from '@/components/lesson/Diagram'
import { parseTutorDiagramSpec } from '@/lib/tutor/tutor-diagram'
import { normalizeMathMarkdown } from '@/lib/render/normalize-math-markdown'
import { sageNearLimitNotice } from '@/lib/tutor/sage-quota'
import { SITE_URL } from '@/lib/seo'
import type { SageAction } from '@unlimitedtests/core'
import { sageActionFromToolResult } from '@/lib/tutor-tools/sage-action-builders'
import {
  useTutorThread,
  type PageContext,
  type TutorMessage,
  type TutorToolCall,
  type TutorToolResult,
} from './useTutorThread'
import ToolCard from './ToolCard'
import { PlanFitCard, SupersededChecks } from './PlanCheckGroup'
import { layoutPlanChecks } from '@/lib/tutor-tools/plan-check-layout'

/**
 * Static fallback suggestions used while the personalized set is loading
 * or if the API call fails. Kept short, generally-applicable prompts.
 */
const FALLBACK_SUGGESTIONS = [
  'What should I study next?',
  'How am I doing overall?',
  'Build me a study plan',
]

export type TutorChatPanelProps = {
  /** Page context to pass to the API on every send. Recomputed by the parent on route change. */
  pageContext?: PageContext
  /** Compact = FAB mode (smaller input, no header). Full = /tutor page. */
  variant?: 'compact' | 'full'
  /** Optional title shown above the messages (full variant only). */
  title?: string
  /**
   * Optional prompt to pre-fill the composer with on mount — used when the
   * tutor is opened from a proactive nudge ("Want me to explain why you missed
   * that?"), so the student lands on a ready-to-send question instead of a
   * blank box. They can edit or just hit send.
   */
  initialInput?: string
  /**
   * Extra page context sent with the auto-sent `initialInput` ONLY — the drill
   * a "What does this mean?" tap is about. Later messages in the same open
   * don't carry it, so Sage follows through once, not on every reply.
   */
  initialInputContext?: Partial<PageContext>
  /**
   * Text put in the composer on mount and NOT sent — an "Ask Sage about this
   * example" button opens Sage pinned to the item with a ready question the
   * student can edit or just send.
   */
  initialDraft?: string
  /**
   * Effective-plan flag (comp-aware, resolved server-side by the mount point).
   * When FALSE we show the free-tier "chats clear after 48 hours" retention
   * notice (the tutor-messages-expire cron enforces it). Defaults to true so
   * mounts that don't know the plan never show a wrong notice.
   */
  isPaid?: boolean
  /**
   * Called when the student opens an activity from a Start card under a reply.
   * The FAB closes its overlay with it, so the panel doesn't sit on top of the
   * session the student just opened.
   */
  onLaunch?: (action: SageAction) => void
}

export default function TutorChatPanel({
  pageContext,
  variant = 'full',
  title = 'Sage',
  initialInput,
  initialInputContext,
  initialDraft,
  isPaid = true,
  onLaunch,
}: TutorChatPanelProps) {
  const { messages, loading, sending, error, dailyRemaining, dailyCap, send, clear } =
    useTutorThread()
  const sageNotice = sageNearLimitNotice(dailyRemaining, dailyCap)
  const [input, setInput] = useState(initialInput ?? initialDraft ?? '')
  const [suggestions, setSuggestions] = useState<string[] | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  // The panel remounts on each open, so this resets per-open: false until the
  // saved history first renders, then true.
  const didInitialScrollRef = useRef(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    // First render with history after opening: land on the NEWEST message,
    // regardless of scroll position. Without this the panel opens scrolled to
    // the top (fresh mount → scrollTop 0 → the near-bottom check below fails,
    // so it never jumps down).
    if (!didInitialScrollRef.current) {
      if (messages.length === 0) return
      didInitialScrollRef.current = true
      const toBottom = () => {
        el.scrollTop = el.scrollHeight
      }
      toBottom()
      // Re-pin after markdown/KaTeX reflow so we don't land a bit short.
      requestAnimationFrame(toBottom)
      return
    }

    // Ongoing updates: stick to the bottom only when the user is already near
    // it — don't yank them down while they've scrolled up to re-read, and skip
    // the per-token "jump" while a reply streams in.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (nearBottom) el.scrollTop = el.scrollHeight
  }, [messages])

  // Composer auto-grow: the input grows with the text (up to a cap, then it
  // scrolls internally) so a long message stays readable instead of clipping in
  // a fixed 2-row box. The user can also drag the handle to make it taller;
  // userMinHeightRef captures that manual size as a floor so typing never shrinks
  // it back down. Heights are px (border-box); lastAutoHeightRef lets the resize
  // observer tell OUR programmatic sets apart from a real drag.
  const AUTO_MAX_PX = 180
  const userMinHeightRef = useRef(0)
  const lastAutoHeightRef = useRef(0)

  const autoResize = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    el.style.height = 'auto'
    const fit = Math.min(el.scrollHeight, AUTO_MAX_PX)
    const next = Math.max(fit, userMinHeightRef.current)
    el.style.height = `${next}px`
    lastAutoHeightRef.current = next
  }, [])

  // Re-fit whenever the text changes (typing, prefill, or clear-on-send).
  useEffect(() => {
    autoResize()
  }, [input, autoResize])

  // Capture a manual drag as the new floor (increase-only). We ignore our own
  // programmatic height sets by comparing against lastAutoHeightRef.
  useEffect(() => {
    const el = inputRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const h = el.offsetHeight
      if (h > lastAutoHeightRef.current + 2) {
        userMinHeightRef.current = h
        lastAutoHeightRef.current = h
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Fetch personalized suggestions for the empty state. Only runs once
  // per mount; if it fails, we render the static fallbacks.
  useEffect(() => {
    let cancelled = false
    const count = variant === 'compact' ? 3 : 4
    fetch(`/api/tutor/suggestions?count=${count}`, { cache: 'no-store' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`status ${r.status}`)
        const data = (await r.json()) as { suggestions?: string[] }
        if (cancelled) return
        if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
          setSuggestions(data.suggestions)
        }
      })
      .catch(() => {
        // Silently fall back — the empty state will show FALLBACK_SUGGESTIONS.
      })
    return () => {
      cancelled = true
    }
  }, [variant])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    const text = input.trim()
    if (!text || sending) return
    setInput('')
    // Drop the manual-drag floor so the composer springs back to its compact
    // size after sending (the [input] effect re-fits once input clears).
    userMinHeightRef.current = 0
    await send(text, pageContext)
    inputRef.current?.focus()
  }

  /** Click handler shared by all suggestion chips. Auto-sends. */
  async function handleSuggestionClick(text: string) {
    if (sending) return
    await send(text, pageContext)
    inputRef.current?.focus()
  }

  /**
   * A nudge-opened panel sends its prompt itself.
   *
   * `initialInput` is only ever set by tapping a nudge peek, and the peek makes
   * a promise: "Stuck? I can give you a hint." Seeding the composer and waiting
   * for a second tap on Send doesn't keep that promise — the student taps, sees
   * a box of pre-typed text, and gets no hint (Visakh report, 2026-07-27).
   *
   * This is also what the emit site already assumed: "the peek is free; only
   * tapping it opens Sage and spends a metered message". It never spent one,
   * because nothing sent.
   *
   * Guarded by a ref rather than a dep list: the panel remounts per open, so
   * once-per-mount is exactly once per nudge tap, and a re-render mid-stream
   * can't fire a second message against the student's daily cap.
   */
  const autoSentRef = useRef(false)
  useEffect(() => {
    const seeded = initialInput?.trim()
    if (!seeded || autoSentRef.current || sending) return
    autoSentRef.current = true
    setInput('')
    void send(
      seeded,
      initialInputContext ? { ...pageContext, ...initialInputContext } : pageContext,
    )
    // `send` and `pageContext` are stable for the life of a mount; re-running on
    // their identity would risk a duplicate send, which the ref already blocks.
  }, [initialInput])

  // An "Ask Sage about this" open lands in the composer, ready to edit or send.
  useEffect(() => {
    if (initialDraft && !initialInput) inputRef.current?.focus()
  }, [initialDraft, initialInput])

  const compact = variant === 'compact'

  /**
   * Wipe the thread, with a confirm so a mis-tap can't destroy a conversation
   * the student wanted. Shared by both variants — the control sits in the
   * header on /tutor and in a slim bar in the FAB, but the action is one thing.
   */
  const confirmClear = useCallback(() => {
    if (confirm('Clear this conversation? Your tutor will still remember your progress.')) {
      void clear()
    }
  }, [clear])

  return (
    <div
      className={
        'flex h-full w-full flex-col bg-background text-foreground ' +
        (compact ? '' : 'rounded-xl border border-border/60')
      }
    >
      {/* Header (full variant only) */}
      {!compact && (
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">{title}</h2>
            {pageContext?.questionId ? (
              /* The thread is ONE rolling conversation, so opening Sage on a
                 practice question shows the PREVIOUS chat — which reads as
                 "it knows nothing about this question" (2026-07-24 report).
                 Say out loud what's attached, so the old history above is
                 obviously history and not Sage's idea of the current topic. */
              <p className="text-xs text-brand-ink">Answering about the question on screen</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Your AI tutor knows your goals, accuracy, and weak spots.
              </p>
            )}
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={confirmClear}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear conversation"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
      )}

      {/* Compact (FAB) control bar.
          The thread is ONE rolling conversation, so opening Sage on a practice
          question shows the PREVIOUS chat. On /tutor the student could always
          clear it from the header above — but the FAB, which is where they
          actually meet this during practice, had no way out: Sage would answer
          "you haven't given me your answer yet" about a question two ago, with
          no visible fix (Visakh report, 2026-07-27).
          TutorFab owns the title/close chrome, so this contributes only the
          action, and only once there is something to clear. */}
      {compact && messages.length > 0 && (
        <div className="flex justify-end border-b border-border/60 px-3 py-1.5">
          <button
            type="button"
            onClick={confirmClear}
            className="inline-flex min-h-[32px] items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Clear conversation"
          >
            <Trash2 className="h-3 w-3" aria-hidden="true" /> Clear chat
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className={
          'flex-1 overflow-y-auto px-4 ' + (compact ? 'py-3 space-y-3 text-sm' : 'py-4 space-y-4')
        }
      >
        {loading && messages.length === 0 && (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <EmptyState
            compact={compact}
            suggestions={suggestions ?? FALLBACK_SUGGESTIONS}
            disabled={sending}
            onSuggestionClick={handleSuggestionClick}
          />
        )}
        {messages.map((m) => (
          <MessageWithTools key={m.id} message={m} onLaunch={onLaunch} />
        ))}
        {error && (
          <div className="rounded-md border border-red-700/40 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {/* Fair-use daily-quota heads-up (hidden while an error is showing). */}
        {!error && sageNotice && (
          <div className="rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
            {sageNotice}
          </div>
        )}
        {/* Free-tier retention notice — chats auto-delete after 48h (enforced
            by the tutor-messages-expire cron). Honest heads-up + upsell. */}
        {!error && !isPaid && messages.length > 0 && (
          <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Free plan: chats clear after 48 hours.{' '}
            <a href="/pricing" className="underline underline-offset-2 hover:text-foreground">
              Upgrade to keep your history.
            </a>
          </div>
        )}
      </div>

      {/* Composer */}
      <form onSubmit={onSubmit} className="border-t border-border/60 px-3 py-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void onSubmit(e as unknown as FormEvent)
              }
            }}
            placeholder={
              compact
                ? 'Ask anything...'
                : 'Ask anything — what to study, why I missed a question, plan changes...'
            }
            rows={compact ? 1 : 2}
            // resize-y = drag handle for manual sizing; max-h caps the drag.
            // overflow-y-auto so text scrolls once it passes the auto-grow cap.
            className="max-h-[60vh] min-h-[40px] flex-1 resize-y overflow-y-auto rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
            disabled={sending}
            aria-label="Message Sage"
          />
          <Button
            type="submit"
            size={compact ? 'sm' : 'default'}
            disabled={!input.trim() || sending}
            className="bg-brand hover:bg-brand-hover text-on-brand"
            aria-label="Send message"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Answers are AI-generated — double-check anything important.
        </p>
      </form>
    </div>
  )
}

/**
 * Tools whose result can be a Start card (ToolCard renders those as a
 * SageActionCard). They're drawn after the other tools' status pills, so the
 * reply reads "here's what I looked at, here's what to do" and the button is
 * the last thing under it — never wedged between two "Pulled progress" pills.
 */
const LAUNCH_TOOL_NAMES: ReadonlySet<string> = new Set(['offer_activity', 'generate_drill'])

/** The Start card this call's stored result builds, by the same check ToolCard makes. */
function launchableAction(call: TutorToolCall, result: TutorToolResult | undefined) {
  if (!LAUNCH_TOOL_NAMES.has(call.name) || !result?.ok) return null
  return sageActionFromToolResult(call.name, result.result)
}

function MessageWithTools({
  message,
  onLaunch,
}: {
  message: TutorMessage
  onLaunch?: (action: SageAction) => void
}) {
  const results = message.toolResults ?? []
  const resultFor = (call: TutorToolCall) => results.find((r) => r.id === call.id)
  const allCalls = message.toolCalls ?? []
  // A first offer can fail (an unknown topic) and Sage's retry succeed in the
  // same reply. The retry's Start card is the answer then, so the failed
  // offer's "Couldn't set that up" note above it is noise. Only a FINISHED
  // offer that built no card is dropped: one still running keeps its
  // "Setting up your practice…" pill, and with no card at all the note stays.
  const hasCard = allCalls.some((c) => launchableAction(c, resultFor(c)) !== null)
  const calls = hasCard
    ? allCalls.filter((c) => {
        if (c.name !== 'offer_activity') return true
        const result = resultFor(c)
        return !result || launchableAction(c, result) !== null
      })
    : allCalls

  // A plan Sage redrafted until it fit: the final passing check is the
  // prominent card under the plan text, and the drafts it replaced collapse
  // (plan-check-layout.ts). The same for a live reply and a reloaded thread.
  // A failed check the reply moved on from to a Start card is set aside under
  // "Didn't fit — options below", so it never reads as the plan.
  const plan = layoutPlanChecks(calls, resultFor, { hasOffer: hasCard })
  const finalFitResult = plan.finalFit ? resultFor(plan.finalFit) : undefined

  // No tools — just the bubble.
  if (calls.length === 0) {
    return (
      <MessageBubble role={message.role} content={message.content} streaming={message.streaming} />
    )
  }

  // Assistant message with tool calls — render bubble + tool cards.
  return (
    <div className="space-y-2">
      {message.content && (
        <MessageBubble
          role={message.role}
          content={message.content}
          streaming={message.streaming}
        />
      )}
      <div className="space-y-1">
        {finalFitResult && <PlanFitCard result={finalFitResult} />}
        <SupersededChecks
          calls={plan.superseded}
          resultFor={resultFor}
          messageId={message.id}
          onLaunch={onLaunch}
        />
        <SupersededChecks
          calls={plan.setAside}
          resultFor={resultFor}
          messageId={message.id}
          onLaunch={onLaunch}
          variant="setAside"
        />
        {[
          ...plan.rest.filter((c) => !LAUNCH_TOOL_NAMES.has(c.name)),
          ...plan.rest.filter((c) => LAUNCH_TOOL_NAMES.has(c.name)),
        ].map((call) => {
          const result = resultFor(call)
          return (
            <ToolCard
              key={call.id}
              call={call}
              result={result}
              messageId={message.id}
              onLaunch={onLaunch}
            />
          )
        })}
      </div>
    </div>
  )
}

function MessageBubble({
  role,
  content,
  streaming,
}: {
  role: 'user' | 'assistant'
  content: string
  streaming?: boolean
}) {
  const isUser = role === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={
          // `min-w-0` allows the flex item to shrink below its content
          // size (otherwise long unbreakable tokens force horizontal
          // overflow). `break-words` wraps long words at word boundaries
          // when possible, and `[overflow-wrap:anywhere]` lets it break
          // anywhere if there's no word boundary (long URLs, dense
          // math/code strings). Together these fix the case Visakh hit
          // where an inline-code caption ran out of the bubble.
          'min-w-0 max-w-[85%] break-words [overflow-wrap:anywhere] rounded-2xl px-4 py-2 ' +
          (isUser ? 'bg-brand text-on-brand' : 'bg-muted text-foreground')
        }
      >
        {content ? (
          isUser ? (
            // User messages render as plain text — they're the student's
            // input, no need for markdown injection.
            <span className="whitespace-pre-wrap">{content}</span>
          ) : (
            // Render markdown live, even mid-stream, so formatting appears as
            // the reply types (closed **bold** etc. never show as raw
            // asterisks). The stream is throttled in useTutorThread so this
            // re-parse stays cheap and smooth.
            <AssistantMarkdown content={content} />
          )
        ) : streaming ? (
          <span className="inline-flex gap-1">
            <span className="animate-pulse">●</span>
            <span className="animate-pulse [animation-delay:200ms]">●</span>
            <span className="animate-pulse [animation-delay:400ms]">●</span>
          </span>
        ) : null}
      </div>
    </div>
  )
}

const SITE_HOST = new URL(SITE_URL).hostname

/**
 * Whether a link in Sage's text points OUTSIDE the app, and so may render as a
 * clickable link. Anything else counts as the app and renders as text:
 *   - a relative link ("/practice/session?…", "practice", "#top", "?x=1"),
 *     including a protocol-relative "//host" (the URL can't be read without a
 *     base, so it isn't trusted either);
 *   - an absolute link to this site or any subdomain of it, or to the host the
 *     page is being served from (staging, localhost);
 *   - any scheme other than http(s) and mailto. react-markdown already blanks
 *     javascript: links; this also drops the rest.
 */
function isOutsideLink(href: string | undefined): href is string {
  if (!href) return false
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return false
  }
  if (url.protocol === 'mailto:') return true
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
  const host = url.hostname.toLowerCase()
  if (host === SITE_HOST || host.endsWith(`.${SITE_HOST}`)) return false
  if (typeof window !== 'undefined' && host === window.location.hostname.toLowerCase()) {
    return false
  }
  return true
}

/**
 * Renders assistant text as markdown with chat-appropriate styling. Tighter
 * spacing than the blog (which uses prose-* classes); tutor responses are
 * conversational, not articles.
 */
export function AssistantMarkdown({ content }: { content: string }) {
  return (
    <div className="text-sm leading-relaxed text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          // Paragraphs: tight margin between, none on first/last
          p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>,
          // Bullet lists
          ul: ({ children }) => (
            <ul className="my-1.5 ml-4 list-disc space-y-0.5 marker:text-brand-ink/60">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-1.5 ml-4 list-decimal space-y-0.5 marker:text-brand-ink/60">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="pl-1">{children}</li>,
          // Bold — bright + slightly weighted
          strong: ({ children }) => (
            <strong className="font-semibold text-brand-ink">{children}</strong>
          ),
          // Italic
          em: ({ children }) => <em className="italic">{children}</em>,
          // Inline code (topic names, formulas, values), block code,
          // AND tutor diagrams. Block code (```) gets className like
          // "language-x"; inline doesn't. The "language-diagram" variant
          // is special-cased: parse the JSON body, dispatch to the
          // shared <Diagram /> component (used by lessons too). Failed
          // parse → fall through to a normal code block, no crash.
          code: ({ children, className }) => {
            const isBlock = !!className

            // Tutor-rendered diagram (see src/lib/tutor-diagram.ts).
            if (isBlock && className === 'language-diagram') {
              const raw = String(children ?? '')
              const result = parseTutorDiagramSpec(raw)
              if (result.ok) {
                return (
                  // max-w-full + overflow-x-auto keeps a wide diagram INSIDE its
                  // own scrollable box instead of pushing the whole Sage panel
                  // to scroll horizontally (the diagram is built for wide lesson
                  // layouts, not this narrow chat column).
                  <div className="my-3 max-w-full overflow-x-auto">
                    <Diagram diagram={result.diagram} />
                  </div>
                )
              }
              // Render the raw JSON in a normal code block + a small
              // muted note so an interested student / debug session can
              // see what went wrong. Doesn't surface to the model.
              return (
                <div className="my-2">
                  <pre className="overflow-x-auto rounded-md bg-background/70 p-3 text-xs">
                    <code>{raw}</code>
                  </pre>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Diagram render skipped: {result.error}
                  </p>
                </div>
              )
            }

            if (isBlock) {
              return (
                <pre className="my-2 overflow-x-auto rounded-md bg-background/70 p-3 text-xs">
                  <code>{children}</code>
                </pre>
              )
            }
            return (
              // [overflow-wrap:anywhere] is needed for inline code with
              // long content (e.g. dense math like "(1,3) and (5,11)
              // showing rise = 8, run = 4"). Without it, the inline
              // code segment forces the bubble to grow past max-w.
              <code className="rounded bg-card/70 px-1 py-0.5 text-[0.85em] text-brand-ink [overflow-wrap:anywhere]">
                {children}
              </code>
            )
          },
          // Headings — rare; only when the model is explaining a concept in depth.
          h1: ({ children }) => (
            <h2 className="mb-1 mt-3 text-base font-semibold text-foreground first:mt-0">
              {children}
            </h2>
          ),
          h2: ({ children }) => (
            <h3 className="mb-1 mt-3 text-sm font-semibold text-foreground first:mt-0">
              {children}
            </h3>
          ),
          h3: ({ children }) => (
            <h4 className="mb-1 mt-2 text-sm font-medium text-foreground first:mt-0">{children}</h4>
          ),
          // Links. An outside page (a doc Sage cites) opens in a new tab. A link
          // into this app renders as plain text: Sage's text is unchecked, and
          // a "[Start](/practice/session?…)" it wrote could name the wrong
          // topic, size or a page that ticks off a study-plan day. Starting
          // something is the Start card's job — built and checked by the
          // server (SageActionCard) — so the text never carries a second,
          // unchecked way in.
          a: ({ children, href }) =>
            isOutsideLink(href) ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-ink underline-offset-2 hover:underline"
              >
                {children}
              </a>
            ) : (
              <span>{children}</span>
            ),
          // Blockquote — rare but supported
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-brand/40 pl-3 italic text-muted-foreground">
              {children}
            </blockquote>
          ),
          // Horizontal rule
          hr: () => <hr className="my-3 border-border/60" />,
        }}
      >
        {normalizeMathMarkdown(content)}
      </ReactMarkdown>
    </div>
  )
}

function EmptyState({
  compact,
  suggestions,
  disabled,
  onSuggestionClick,
}: {
  compact: boolean
  suggestions: string[]
  disabled: boolean
  onSuggestionClick: (text: string) => void
}) {
  return (
    <div
      className={
        'flex flex-col items-center justify-center text-center text-muted-foreground ' +
        (compact ? 'py-6' : 'py-12')
      }
    >
      <p className={'font-medium text-muted-foreground ' + (compact ? 'text-sm' : 'text-base')}>
        Hey! I&apos;m Sage, your AI tutor.
      </p>
      <p className={'mt-1 text-muted-foreground ' + (compact ? 'text-xs' : 'text-sm')}>
        Ask me anything about your prep — I know what you&apos;ve been working on.
      </p>
      {suggestions.length > 0 && (
        <div
          className={
            'flex flex-wrap justify-center gap-2 ' + (compact ? 'mt-4 text-[11px]' : 'mt-6 text-xs')
          }
        >
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onSuggestionClick(s)}
              disabled={disabled}
              className={
                'rounded-full border border-border bg-card/40 text-muted-foreground transition-colors ' +
                'hover:border-brand/60 hover:bg-brand/10 hover:text-brand-ink dark:hover:text-brand-ink ' +
                'focus:outline-none focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/40 ' +
                'disabled:cursor-not-allowed disabled:opacity-50 ' +
                (compact ? 'px-2.5 py-1' : 'px-3 py-1.5')
              }
              aria-label={`Send: ${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
